import { useElementRender, type ElementComponent } from '../render/elementRenderContext';
import { boolProp, numberProp, textProp } from '../render/elementProps';
import { useFieldBox } from './FieldFrame';
import { StarRating, starCount } from './StarRating';
import { useFormField } from './useFormField';
import './forms.css';

/**
 * A rating a visitor gives. This is the input, not the display — `product-rating` in `elements/commerce`
 * is the score a shop has already earned, read off the server's own aggregate, and nothing here draws
 * that. A merchant who ticks "Show only, do not ask" gets the same row without the radios in it.
 *
 * The chosen score is kept as text, because that is what a form submits and what a review posts; the row
 * itself only ever deals in whole stars.
 */

// Draws a rating being asked for, or shown, depending on what the merchant asked this row to be.
export const StarsElement: ElementComponent = ({ node }) => {
  const max = starCount(numberProp(node.props, 'max', 5));
  const asks = !boolProp(node.props, 'readOnly', false);
  const field = useFormField(node, { shape: 'none', empty: String(numberProp(node.props, 'rating', 0)) });
  const box = useFieldBox(node);
  const { editing, store } = useElementRender();
  const caption = textProp(node.props, 'label');
  // A product's score shown with no reviews behind it is five empty stars; Sleek shows nothing, and so does a live page here.
  if (!asks && !editing && node.bindings?.rating === 'product.rating' && numberProp(node.props, 'rating', 0) <= 0) return null;
  return (
    <div className={asks ? 'xs-form-field' : 'xs-form-field xs-form-field--shown'} {...box.marks}>
      {caption === '' ? null : (
        <span className="xs-form-caption" dir="auto">{caption}{field.rule.required ? ' *' : ''}</span>
      )}
      <StarRating
        name={field.rule.domId}
        value={Number(field.value) || 0}
        max={max}
        label={caption || field.rule.name}
        className={box.className}
        language={store?.language}
        invalid={field.error !== ''}
        describedBy={field.error === '' ? undefined : field.errorId}
        onChange={asks ? (score) => field.onChange(String(score)) : undefined}
      />
      {field.error === '' ? null : <span className="xs-form-error" id={field.errorId} dir="auto">{field.error}</span>}
    </div>
  );
};
